import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  filterAndSort, sortArr, money, formatDate, estadoClass,
  clienteName, proyectoName, ventaBase, ventaTotal, proyectoTotal,
} from "@/lib/helpers";
import { exportExcel, exportPDF, ventaPDF, ventaExcel } from "@/lib/exports";
import PaginationBar from "@/components/PaginationBar";
import DatePicker from "@/components/DatePicker";

const blank = () => ({
  numero: "",
  fecha: new Date().toISOString().slice(0, 10),
  estado: "Borrador",
  clienteId: "",
  proyectoId: "",
  items: [],
  serviciosItems: [],
  iva: 21,
  total: 0,
});

const ESTADOS = ["Borrador", "Confirmada", "Facturada", "Cobrada"];

export default function Ventas({ state, upsert, remove }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "fecha", dir: -1 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blank());
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: -s.dir } : { field, dir: 1 }));

  const allRows = useMemo(
    () => filterAndSort(state.ventas, search, sort, state),
    [state.ventas, search, sort, state]
  );
  const pagedRows = allRows.slice(skip, skip + limit);

  // On mount: if we have a prefill from "→ Venta" button in Proyectos, open modal
  useEffect(() => {
    const pre = sessionStorage.getItem("convitel_venta_prefill");
    if (pre) {
      sessionStorage.removeItem("convitel_venta_prefill");
      const data = JSON.parse(pre);
      const b = blank();
      b.numero = "V-" + String(state.ventas.length + 1).padStart(4, "0");
      b.clienteId = data.clienteId || "";
      b.proyectoId = data.proyectoId || "";
      b.items = data.items || [];
      b.serviciosItems = data.serviciosItems || [];
      setForm(b);
      setModal(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (row = null) => {
    const b = blank();
    if (row) {
      const data = { ...b, ...row };
      data.items = data.items || [];
      data.serviciosItems = data.serviciosItems || [];
      if (data.iva == null) data.iva = 21;
      setForm(data);
    } else {
      b.numero = "V-" + String(state.ventas.length + 1).padStart(4, "0");
      setForm(b);
    }
    setModal(true);
  };

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { articuloId: "", cantidad: 1, precio: 0, nombre: "", codigo: "" }] }));
  const addServ = () =>
    setForm((f) => ({ ...f, serviciosItems: [...f.serviciosItems, { servicioId: "", cantidad: 1, precio: 0, nombre: "", codigo: "" }] }));

  const updateLine = (list, i, patch) =>
    setForm((f) => ({ ...f, [list]: f[list].map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  const removeLine = (list, i) =>
    setForm((f) => ({ ...f, [list]: f[list].filter((_, idx) => idx !== i) }));

  const onSelectArticulo = (i, articuloId) => {
    const a = state.articulos.find((x) => x.id === articuloId);
    const patch = { articuloId };
    if (a) { patch.nombre = a.nombre || ""; patch.codigo = a.codigo || ""; patch.precio = a.precio || 0; }
    updateLine("items", i, patch);
  };
  const onSelectServicio = (i, servicioId) => {
    const s = state.servicios.find((x) => x.id === servicioId);
    const patch = { servicioId };
    if (s) { patch.nombre = s.nombre || ""; patch.codigo = s.codigo || ""; patch.precio = s.precio || 0; }
    updateLine("serviciosItems", i, patch);
  };

  const proyectosCliente = useMemo(() => {
    if (!form.clienteId) return state.proyectos;
    return state.proyectos.filter((p) => p.clienteId === form.clienteId);
  }, [form.clienteId, state.proyectos]);

  const cargarProyectoEnVenta = () => {
    const p = state.proyectos.find((x) => x.id === form.proyectoId);
    if (!p) { toast.error("Selecciona un proyecto"); return; }
    setForm((f) => ({
      ...f,
      clienteId: p.clienteId,
      items: (p.articulos || []).map((l) => ({
        articuloId: l.articuloId, cantidad: +l.cantidad || 0, precio: +l.precio || 0,
        nombre: l.nombre || "", codigo: l.codigo || "",
      })),
      serviciosItems: (p.servicios || []).map((l) => ({
        servicioId: l.servicioId, cantidad: +l.cantidad || 0, precio: +l.precio || 0,
        nombre: l.nombre || "", codigo: l.codigo || "",
      })),
    }));
    toast.success(`Cargados ${(p.articulos || []).length} artículos y ${(p.servicios || []).length} servicios`);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, total: ventaTotal(form) };
    await upsert("ventas", payload);
    toast.success("Venta guardada");
    setModal(false);
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <input className="input max-w-xs" placeholder="Buscar venta..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="search-ventas"/>
        <div className="flex-1"/>
        <button className="btn btn-ghost" onClick={() => exportExcel("ventas", state) || toast.error("Sin datos")}>Excel</button>
        <button className="btn btn-ghost" onClick={() => exportPDF("ventas", state) || toast.error("Sin datos")}>PDF</button>
        <button className="btn btn-primary" onClick={() => openModal()} data-testid="btn-new-venta">+ Nueva venta</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            {[
              ["numero","Nº"],["fecha","Fecha"],["cliente","Cliente"],
              ["proyecto","Proyecto"],["nLineas","Líneas"],["total","Total"],["estado","Estado"],
            ].map(([k, l]) => (
              <th key={k} className={`sortable ${["nLineas","total"].includes(k) ? "text-right " : ""}${sort.field === k ? "active" : ""}`}
                  onClick={() => toggleSort(k)}>
                {l} <span className="arr">{sortArr(sort, k)}</span>
              </th>
            ))}
            <th/>
          </tr></thead>
          <tbody>
            {pagedRows.map((v) => (
              <tr key={v.id}>
                <td className="font-semibold">{v.numero}</td>
                <td>{formatDate(v.fecha)}</td>
                <td>{clienteName(state, v.clienteId)}</td>
                <td>{proyectoName(state, v.proyectoId)}</td>
                <td className="text-right">{(v.items || []).length + (v.serviciosItems || []).length}</td>
                <td className="text-right font-bold text-[var(--cvt-orange)]">{money(v.total)}</td>
                <td><span className={`badge ${estadoClass(v.estado)}`}>{v.estado}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn btn-ghost !py-1 !px-2" onClick={() => openModal(v)}>Editar</button>
                  <button className="btn btn-ghost !py-1 !px-2" title="Descargar PDF"
                          onClick={() => ventaPDF(v, state)} data-testid={`venta-pdf-${v.id}`}>PDF</button>
                  <button className="btn btn-ghost !py-1 !px-2" title="Descargar Excel"
                          onClick={() => ventaExcel(v, state)} data-testid={`venta-xlsx-${v.id}`}>Excel</button>
                  <button className="btn btn-danger !py-1 !px-2" onClick={() => remove("ventas", v.id)}>✕</button>
                </td>
              </tr>
            ))}
            {allRows.length === 0 && (
              <tr><td colSpan="8" className="text-center text-slate-400 py-6">No hay ventas</td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar total={allRows.length} skip={skip} limit={limit}
                       onSkipChange={setSkip} onLimitChange={setLimit}/>
      </div>

      {modal && (
        <div className="modal-backdrop">
          <div className="modal p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nueva"} venta</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="space-y-5">
              <div className="grid md:grid-cols-4 gap-4">
                <div><label className="label">Nº Venta *</label>
                  <input className="input" required value={form.numero || ""} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="venta-numero" placeholder="V-0001"/></div>
                <div><label className="label">Fecha</label>
                  <DatePicker value={form.fecha || ""} onChange={(v) => setForm({ ...form, fecha: v })} testid="venta-fecha"/></div>
                <div><label className="label">Estado</label>
                  <select className="select" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                    {ESTADOS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="label">Cliente *</label>
                  <select className="select" required value={form.clienteId || ""} onChange={(e) => setForm({ ...form, clienteId: e.target.value })} data-testid="venta-cliente">
                    <option value="">Selecciona...</option>
                    {state.clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4">
                  <label className="label">Proyecto vinculado (opcional — al seleccionar se cargan sus artículos y servicios)</label>
                  <div className="flex gap-2">
                    <select className="select" value={form.proyectoId || ""} onChange={(e) => setForm({ ...form, proyectoId: e.target.value })} data-testid="venta-proyecto">
                      <option value="">— Sin proyecto —</option>
                      {proyectosCliente.map((p) => (
                        <option key={p.id} value={p.id}>
                          {`${p.nombre} · ${clienteName(state, p.clienteId)} · ${money(proyectoTotal(p))}`}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-blue" onClick={cargarProyectoEnVenta}
                            disabled={!form.proyectoId} data-testid="venta-cargar-proyecto">
                      Cargar artículos y servicios del proyecto
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Puedes editar cantidades/precios antes de confirmar.</div>
                </div>
              </div>

              {/* Artículos */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold">Artículos</div>
                  <button type="button" className="btn btn-blue !py-1.5" onClick={addItem} data-testid="venta-add-articulo">+ Artículo</button>
                </div>
                <table className="data">
                  <thead><tr>
                    <th>Artículo</th><th className="w-28 text-right">Cantidad</th>
                    <th className="w-32 text-right">Precio</th>
                    <th className="w-32 text-right">Subtotal</th><th className="w-10"/>
                  </tr></thead>
                  <tbody>
                    {form.items.map((line, i) => (
                      <tr key={i}>
                        <td>
                          <select className="select" value={line.articuloId || ""} onChange={(e) => onSelectArticulo(i, e.target.value)}>
                            <option value="">Selecciona artículo...</option>
                            {state.articulos.map((a) => (
                              <option key={a.id} value={a.id}>{(a.codigo || "") + " · " + a.nombre}</option>
                            ))}
                            {line.articuloId && !state.articulos.find((a) => a.id === line.articuloId) && (line.nombre || line.codigo) && (
                              <option value={line.articuloId}>{(line.codigo || "") + " · " + (line.nombre || "(sin nombre)") + " · (fuera de catálogo)"}</option>
                            )}
                          </select>
                          {line.nombre && (
                            <div className="text-[11px] text-slate-500 mt-1">
                              {line.codigo ? line.codigo + " · " : ""}{line.nombre || ""}
                            </div>
                          )}
                        </td>
                        <td><input type="number" step="1" min="0" className="input text-right"
                                   value={line.cantidad} onChange={(e) => updateLine("items", i, { cantidad: +e.target.value })}/></td>
                        <td><input type="number" step="0.01" className="input text-right"
                                   value={line.precio} onChange={(e) => updateLine("items", i, { precio: +e.target.value })}/></td>
                        <td className="text-right font-semibold">{money((+line.cantidad || 0) * (+line.precio || 0))}</td>
                        <td><button type="button" className="text-red-500 hover:text-red-700" onClick={() => removeLine("items", i)}>✕</button></td>
                      </tr>
                    ))}
                    {form.items.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-slate-400 py-2 text-sm">Sin artículos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Servicios */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold">Servicios</div>
                  <button type="button" className="btn btn-blue !py-1.5" onClick={addServ}>+ Servicio</button>
                </div>
                <table className="data">
                  <thead><tr>
                    <th>Servicio</th><th className="w-28 text-right">Cantidad</th>
                    <th className="w-32 text-right">Precio</th>
                    <th className="w-32 text-right">Subtotal</th><th className="w-10"/>
                  </tr></thead>
                  <tbody>
                    {form.serviciosItems.map((line, i) => (
                      <tr key={i}>
                        <td>
                          <select className="select" value={line.servicioId || ""} onChange={(e) => onSelectServicio(i, e.target.value)}>
                            <option value="">Selecciona servicio...</option>
                            {state.servicios.map((s) => (
                              <option key={s.id} value={s.id}>{(s.codigo || "") + " · " + s.nombre}</option>
                            ))}
                            {line.servicioId && !state.servicios.find((s) => s.id === line.servicioId) && (line.nombre || line.codigo) && (
                              <option value={line.servicioId}>{(line.codigo || "") + " · " + (line.nombre || "(sin nombre)") + " · (fuera de catálogo)"}</option>
                            )}
                          </select>
                          {line.nombre && (
                            <div className="text-[11px] text-slate-500 mt-1">
                              {line.codigo ? line.codigo + " · " : ""}{line.nombre || ""}
                            </div>
                          )}
                        </td>
                        <td><input type="number" step="0.5" min="0" className="input text-right"
                                   value={line.cantidad} onChange={(e) => updateLine("serviciosItems", i, { cantidad: +e.target.value })}/></td>
                        <td><input type="number" step="0.01" className="input text-right"
                                   value={line.precio} onChange={(e) => updateLine("serviciosItems", i, { precio: +e.target.value })}/></td>
                        <td className="text-right font-semibold">{money((+line.cantidad || 0) * (+line.precio || 0))}</td>
                        <td><button type="button" className="text-red-500 hover:text-red-700" onClick={() => removeLine("serviciosItems", i)}>✕</button></td>
                      </tr>
                    ))}
                    {form.serviciosItems.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-slate-400 py-2 text-sm">Sin servicios</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card p-4 bg-gradient-to-r from-orange-50 to-blue-50">
                <div className="flex flex-wrap items-center justify-end gap-6">
                  <div className="text-sm"><span className="text-slate-500">Base:</span> <span className="font-semibold">{money(ventaBase(form))}</span></div>
                  <div className="text-sm flex items-center gap-2">
                    <span className="text-slate-500">IVA %:</span>
                    <input type="number" step="1" className="input w-20 text-right"
                           value={form.iva} onChange={(e) => setForm({ ...form, iva: +e.target.value })}/>
                  </div>
                  <div className="text-sm"><span className="text-slate-500">IVA:</span> <span className="font-semibold">{money(ventaBase(form) * (form.iva || 0) / 100)}</span></div>
                  <div className="text-xl font-extrabold text-[var(--cvt-orange)]">TOTAL <span>{money(ventaTotal(form))}</span></div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="venta-save">Guardar venta</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
